import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Scale, FileWarning, Search as SearchIcon, Link2, Mail, AlertTriangle,
  Globe, Database, ShieldOff, Gavel,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import useSeo from "@/hooks/useSeo";
import Footer from "@/components/Footer";

const SECTIONS = [
  {
    Icon: Database,
    title: "What TorHub actually does",
    accent: "#FF2A6D",
    body: (
      <>
        <p>
          TorHub is a <strong className="text-white">metadata search and discovery layer</strong>. We index
          publicly-available information about movies and TV shows (titles, posters, ratings, runtimes,
          synopses) sourced from third-party APIs like TMDB and IMDb. We also aggregate publicly-available
          magnet links from independent torrent indexers such as YTS, EZTV, 1337x, TorrentGalaxy, Knaben,
          ext.to and UIndex.
        </p>
        <p className="mt-3">
          We do <strong className="text-white">not</strong> upload, host, store, transmit, seed, leech, mirror
          or otherwise distribute any video, audio, or copyrighted file. No <code>.torrent</code> files and no
          media are stored on TorHub&apos;s servers. The only data we persist is user accounts, comments, karma,
          and a transient cache of metadata returned by the third parties above.
        </p>
      </>
    ),
  },
  {
    Icon: Link2,
    title: "Magnet links are addresses, not content",
    accent: "#FFD700",
    body: (
      <>
        <p>
          A magnet link is an identifier — a string that points to a swarm of peers operated by users we have
          no relationship with. TorHub never participates in any such swarm. Clicking a magnet link is
          analogous to following a hyperlink: the file transfer happens between you, the BitTorrent network,
          and other peers — not between you and TorHub.
        </p>
        <p className="mt-3">
          Whether a particular swarm is legal to join depends on the content being shared and the laws of your
          jurisdiction. TorHub takes no position on, and accepts no responsibility for, what you choose to
          download.
        </p>
      </>
    ),
  },
  {
    Icon: ShieldOff,
    title: "No warranties, no liability",
    accent: "#FF6B35",
    body: (
      <>
        <p>
          TorHub is provided <strong className="text-white">&ldquo;as is&rdquo;, without warranty of any kind</strong>,
          express or implied, including but not limited to warranties of merchantability, fitness for a
          particular purpose, accuracy of metadata, availability of magnet links, or non-infringement.
        </p>
        <p className="mt-3">
          To the maximum extent permitted by applicable law, the site owner, operators and contributors shall
          not be liable for any direct, indirect, incidental, special, consequential or punitive damages
          arising from your use of, or inability to use, this site — including damages for lost data, malware,
          ISP enforcement actions, copyright claims, or any third-party content you obtain through links
          discovered here.
        </p>
      </>
    ),
  },
  {
    Icon: Globe,
    title: "You are responsible for your jurisdiction",
    accent: "#6D4AFF",
    body: (
      <>
        <p>
          BitTorrent itself is a perfectly legal protocol used by Linux distributions, game patchers,
          scientific datasets, the Internet Archive, and many others. Whether a specific piece of content can
          be lawfully downloaded, streamed, or shared in your country is a separate question — and one only
          you can answer.
        </p>
        <p className="mt-3">
          By using TorHub you confirm that you understand the laws of your jurisdiction and accept full and
          sole responsibility for your actions. If your country prohibits accessing torrent indexers, you must
          stop using TorHub immediately.
        </p>
      </>
    ),
  },
  {
    Icon: FileWarning,
    title: "DMCA &amp; copyright takedowns",
    accent: "#DC143C",
    body: (
      <>
        <p>
          TorHub does not host any of the underlying copyrighted material — we cannot &ldquo;take down&rdquo; a
          file we never had. However, we&apos;re happy to <strong className="text-white">remove specific
          magnet links and TMDB references</strong> from our index when presented with a valid
          DMCA-style notice from the rights holder or their authorised representative.
        </p>
        <p className="mt-3">A valid notice must include:</p>
        <ul className="list-disc list-inside space-y-1 mt-2 text-white/80">
          <li>A clear identification of the copyrighted work claimed to be infringed.</li>
          <li>The specific TorHub URL(s) or magnet info-hash(es) you want removed.</li>
          <li>Your contact details (name, email, postal address) and a statement of good-faith belief.</li>
          <li>A statement, under penalty of perjury, that you are the owner or authorised agent.</li>
          <li>Your physical or electronic signature.</li>
        </ul>
        <p className="mt-3">
          Send notices to the address in the contact section below. Frivolous, automated or boilerplate
          claims will be answered but not actioned.
        </p>
      </>
    ),
  },
  {
    Icon: SearchIcon,
    title: "Third-party trademarks &amp; brands",
    accent: "#22C77A",
    body: (
      <>
        <p>
          TMDB®, IMDb®, Trakt®, Letterboxd® and the names/logos of every torrent indexer, BitTorrent client
          and affiliate partner mentioned anywhere on this site are the property of their respective owners.
          Their inclusion is purely <strong className="text-white">nominative</strong> — to identify the
          source or recipient of information — and does not imply any endorsement, partnership, or
          affiliation with TorHub unless explicitly stated.
        </p>
      </>
    ),
  },
  {
    Icon: Gavel,
    title: "Changes to this disclaimer",
    accent: "#2BD9A5",
    body: (
      <>
        <p>
          We may update this page from time to time to reflect changes in our practices, applicable law, or
          third-party providers. The &ldquo;Last updated&rdquo; date at the top of this page indicates when
          the most recent change was made. Continued use of TorHub after a change constitutes acceptance of
          the updated terms.
        </p>
      </>
    ),
  },
];

export default function DisclaimerPage() {
  useSeo({
    title: "Disclaimer &amp; Copyright Notice — TorHub",
    description:
      "TorHub indexes metadata and magnet links — it hosts no copyrighted content. Read our full disclaimer, liability disclaimer, jurisdiction notice and DMCA takedown procedure.",
    path: "/disclaimer",
  });

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header />
        <main className="px-4 md:px-10 py-8 flex-1 w-full max-w-4xl mx-auto">
          <Link
            to="/"
            data-testid="disclaimer-back-home"
            className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-2 text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          {/* Hero */}
          <header className="mb-10">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[var(--tt-primary)] font-bold mb-3">
              <Scale className="w-3.5 h-3.5" />
              Legal &amp; Disclaimer
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.95] tracking-tight">
              We index links.
              <br />
              <span className="text-[var(--tt-primary)]">We don&apos;t host files.</span>
            </h1>
            <p className="text-sm md:text-base text-[var(--tt-text-muted)] mt-5 max-w-2xl leading-relaxed">
              TorHub is a search and discovery layer for publicly-available movie and TV metadata and magnet
              links. The site owner is not the publisher of, nor responsible for, any third-party content
              reachable through links discovered here. The full version of that statement is below.
            </p>
            <div className="mt-5 text-[11px] uppercase tracking-[0.18em] text-[var(--tt-text-faint)] font-mono-tt">
              Last updated · February 2026
            </div>
          </header>

          {/* TL;DR card */}
          <section
            data-testid="disclaimer-tldr"
            className="mb-10 rounded-3xl border border-[var(--tt-yellow)]/40 bg-gradient-to-br from-[rgba(255,215,0,0.06)] via-transparent to-[rgba(255,42,109,0.05)] backdrop-blur-md p-6 md:p-8"
          >
            <div className="flex items-start gap-4">
              <div className="hidden md:flex w-12 h-12 rounded-2xl bg-[var(--tt-yellow)]/15 border border-[var(--tt-yellow)]/40 items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-[var(--tt-yellow)]" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-yellow)] mb-2">
                  TL;DR
                </div>
                <ul className="space-y-2 text-sm text-white/85 leading-relaxed">
                  <li className="flex gap-2">
                    <span className="text-[var(--tt-yellow)] font-bold flex-shrink-0">→</span>
                    <span>TorHub <strong>does not host, store, upload, or distribute</strong> any copyrighted media.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--tt-yellow)] font-bold flex-shrink-0">→</span>
                    <span>We index <strong>publicly-available metadata</strong> (TMDB/IMDb) and <strong>publicly-available magnet links</strong> from independent torrent indexers.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--tt-yellow)] font-bold flex-shrink-0">→</span>
                    <span>The site owner accepts <strong>no liability</strong> for what users do with those links.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--tt-yellow)] font-bold flex-shrink-0">→</span>
                    <span>You are responsible for complying with your local laws.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--tt-yellow)] font-bold flex-shrink-0">→</span>
                    <span>Rights holders can submit a DMCA-style notice to have specific links de-indexed.</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Sections */}
          <ol className="space-y-3" data-testid="disclaimer-sections">
            {SECTIONS.map((s, idx) => (
              <li
                key={s.title}
                data-testid={`disclaimer-section-${idx}`}
                className="group relative rounded-3xl border border-[var(--tt-border)] bg-[var(--tt-surface)]/70 backdrop-blur-md p-5 md:p-6 overflow-hidden hover:border-[var(--tt-primary)]/30 transition-colors"
              >
                <div
                  className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-20 blur-3xl pointer-events-none"
                  style={{ background: s.accent }}
                />
                <div className="relative flex items-start gap-4">
                  <div className="flex-shrink-0 hidden sm:flex flex-col items-center gap-2">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${s.accent}33, transparent 60%), #0b0b0b`,
                        boxShadow: `inset 0 0 0 1px ${s.accent}55`,
                      }}
                    >
                      <s.Icon className="w-5 h-5" style={{ color: s.accent }} />
                    </div>
                    <span className="font-mono-tt text-[10px] text-[var(--tt-text-muted)]">
                      §{String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2
                      className="font-display text-lg md:text-xl font-extrabold tracking-tight mb-2"
                      // headings may contain entity refs (&amp;) decoded inline
                      dangerouslySetInnerHTML={{ __html: s.title }}
                    />
                    <div className="text-sm text-white/80 leading-relaxed space-y-2">{s.body}</div>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          {/* Contact card */}
          <section
            data-testid="disclaimer-contact"
            className="mt-10 rounded-3xl border border-[var(--tt-border)] bg-[var(--tt-surface)]/70 backdrop-blur-md p-6 md:p-8"
          >
            <div className="flex items-start gap-4">
              <div className="hidden md:flex w-12 h-12 rounded-2xl bg-[var(--tt-primary)]/15 border border-[var(--tt-primary)]/30 items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-[var(--tt-primary)]" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg md:text-xl font-extrabold mb-2">
                  Takedown &amp; legal contact
                </h3>
                <p className="text-sm text-white/75 leading-relaxed">
                  Send DMCA-style notices, legal correspondence and abuse reports to:
                </p>
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-black/40 border border-[var(--tt-border)] px-4 py-2 font-mono-tt text-sm">
                  <Mail className="w-3.5 h-3.5 text-[var(--tt-primary)]" />
                  <a
                    href="mailto:abuse@torhub.me"
                    className="text-[var(--tt-primary)] hover:text-white"
                    data-testid="disclaimer-contact-email"
                  >
                    abuse@torhub.me
                  </a>
                </div>
                <p className="text-[11px] text-[var(--tt-text-faint)] mt-3 leading-relaxed">
                  Please allow up to 7 business days for a response. Frivolous or automated notices may be
                  forwarded to the EFF for review.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Link
                    to="/rules"
                    data-testid="disclaimer-to-rules"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-white/70 hover:text-white px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-white/30"
                  >
                    Community Rules
                  </Link>
                  <Link
                    to="/software"
                    data-testid="disclaimer-to-software"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-white/70 hover:text-white px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-white/30"
                  >
                    BitTorrent Clients
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  );
}
